// src/pages/bc/BcReview.tsx — Business Control review, grouped by SME decision status.
//
// Status is the primary axis (what the SME must do); lane becomes a facet (how the agent
// derived it). The disposition ribbon shows the whole population at a glance and is the
// navigation. Ready splits into rule-ready / review-suggested / assessment-pending. A
// control's full detail opens in a slide-over beside the list, with actions that match its
// status. onOpenEkd(target) navigates to Enterprise Knowledge / the lookup layer for a link.
import React, { useCallback, useEffect, useState } from "react";
import {
  bc_getStatusSummary, bc_getStatus, bc_getControl, bc_review, bc_getControlPreview,
  type BcStatusSummary, type BcStatusResponse, type BcStatusKey, type BcControlView, type BcRulePreview,
} from "../api/bcAPI";

const STATUS_META: Record<BcStatusKey, { label: string; cls: string }> = {
  needs_you:       { label: "Needs you",       cls: "s-need" },
  awaiting_values: { label: "Awaiting values", cls: "s-await" },
  ready:           { label: "Ready",           cls: "s-ready" },
  excluded:        { label: "Excluded",        cls: "s-excl" },
};
const READY_SUB_CLS: Record<string, string> = {
  rule_ready: "sub-ready", review_suggested: "sub-review", assessment_pending: "sub-pending",
};

export default function BcReview({ entityId, entityLabel, onOpenEkd }: {
  entityId: string; entityLabel?: string;
  onOpenEkd?: (t: { kind: "reference_domain" | "field" | "gap"; id: string; lookup_entity_id?: string }) => void;
}) {
  const [summary, setSummary] = useState<BcStatusSummary | null>(null);
  const [status, setStatus] = useState<BcStatusKey>("needs_you");
  const [sub, setSub] = useState<string | null>(null);
  const [laneF, setLaneF] = useState<string | null>(null);
  const [data, setData] = useState<BcStatusResponse | null>(null);
  const [detail, setDetail] = useState<BcControlView | null>(null);
  const [preview, setPreview] = useState<BcRulePreview | null>(null);
  const [pvBusy, setPvBusy] = useState(false);
  const [q, setQ] = useState(""); const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const note = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2200); };

  const loadSummary = useCallback(async () => { try { setSummary(await bc_getStatusSummary(entityId)); } catch { setSummary(null); } }, [entityId]);
  const loadStatus = useCallback(async (s: BcStatusKey, sb: string | null, ln: string | null) => {
    try { setData(await bc_getStatus(entityId, s, sb || undefined, ln || undefined)); } catch { setData(null); }
  }, [entityId]);

  useEffect(() => { loadSummary(); }, [entityId]);                    // eslint-disable-line
  useEffect(() => { loadStatus(status, sub, laneF); setDetail(null); }, [status, sub, laneF, entityId]); // eslint-disable-line

  // Default-select the first control whenever the list (re)loads, so the slide-over
  // is never empty when the SME lands on a status.
  useEffect(() => { if (data?.controls?.length) openDetail(data.controls[0]); else setDetail(null); }, [data]); // eslint-disable-line

  // Fetch the compiled-rule + sample-data preview for the open control.
  useEffect(() => {
    if (!detail) { setPreview(null); return; }
    let live = true; setPvBusy(true); setPreview(null);
    bc_getControlPreview(entityId, detail.control_id)
      .then((p) => { if (live) setPreview(p); })
      .catch(() => { if (live) setPreview(null); })
      .finally(() => { if (live) setPvBusy(false); });
    return () => { live = false; };
  }, [detail?.control_id, entityId]); // eslint-disable-line

  const goStatus = (s: BcStatusKey, sb: string | null = null) => { setStatus(s); setSub(sb); setLaneF(null); };
  const openDetail = async (c: BcControlView) => { try { const r = await bc_getControl(entityId, c.control_id); setDetail({ ...c, ...r.control }); } catch { setDetail(c); } };
  const act = async (c: BcControlView, st: string) => {
    setBusy(c.control_id);
    try {
      await bc_review(entityId, c.control_id, st);
      await Promise.all([loadStatus(status, sub, laneF), loadSummary()]);
      if (detail?.control_id === c.control_id) setDetail({ ...detail, status: st });
      note(st === "confirmed" ? "Confirmed → rule-eligible." : st === "flagged" ? "Flagged for review." : "Reset.");
    } catch { note("Couldn't save. Try again."); } finally { setBusy(null); }
  };

  const pct = (n?: number) => (n == null ? "—" : Math.round(n * 100) + "%");
  const controls = (data?.controls || []).filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase()) || (c.owner_field || "").toLowerCase().includes(q.toLowerCase()));
  const stt = summary?.statuses;
  const seg = (k: BcStatusKey) => (stt ? stt[k].count : 0);
  const ny = stt?.needs_you;

  const Ribbon = () => {
    if (!stt || !summary) return null;
    const total = Math.max(1, summary.total);
    return (
      <div className="bc-ribbon" role="tablist" aria-label="Control disposition">
        {summary.order.map((k) => {
          const c = seg(k); if (!c) return null;
          return <button key={k} role="tab" aria-selected={status === k} title={STATUS_META[k].label + " · " + c}
            className={"bc-seg " + STATUS_META[k].cls + (status === k ? " on" : "")}
            style={{ flexGrow: c, flexBasis: 0 }} onClick={() => goStatus(k)} aria-label={STATUS_META[k].label + ", " + c + " of " + total} />;
        })}
      </div>
    );
  };

  const StatusTabs = () => (
    <div className="bc-stabs">
      {summary?.order.map((k) => {
        const m = STATUS_META[k], c = seg(k);
        const badge = k === "needs_you" && ny && ny.pending !== c ? ny.pending + "/" + c : "" + c;
        return (
          <button key={k} className={"bc-stab " + m.cls + (status === k ? " on" : "")} onClick={() => goStatus(k)}>
            <span className="bc-dot" />
            <span className="bc-stab-n">{badge}</span>
            <span className="bc-stab-l">{m.label}</span>
          </button>
        );
      })}
    </div>
  );

  const Facets = () => {
    if (!data?.facets?.items?.length) return null;
    const isReady = data.facets.kind === "ready_sub";
    const active = isReady ? sub : laneF;
    const clear = () => (isReady ? setSub(null) : setLaneF(null));
    return (
      <div className="bc-facets">
        <button className={"bc-fc" + (!active ? " on" : "")} onClick={clear}>All {data.label.toLowerCase()} {data.counts.total}</button>
        {data.facets.items.map((f) => f.count > 0 ? (
          <button key={f.key} className={"bc-fc " + (isReady ? (READY_SUB_CLS[f.key] || "") : "") + (active === f.key ? " on" : "")}
            onClick={() => (isReady ? setSub(f.key) : setLaneF(f.key))}>{f.label} {f.count}</button>
        ) : null)}
        <span className="sp" />
        <input className="bc-search" placeholder="Filter by title or field" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
    );
  };

  const RulePreview = () => {
    if (pvBusy) return <div className="bc-rp"><div className="bc-rp-h">Rule &amp; impact</div><div className="bc-rp-load">Compiling and running on sample data…</div></div>;
    if (!preview) return null;
    if (preview.engine_available === false)
      return <div className="bc-rp"><div className="bc-rp-h">Rule &amp; impact</div><div className="bc-rp-nodata">{preview.execution_note}</div></div>;
    const ex = preview.execution;
    const rule = preview.rule;
    const enf = rule?.enforcement || preview.compilation?.enforcement;
    const applicable = ex?.applicable_records ?? ex?.total_records ?? 0;
    const failed = ex?.records_failed || 0;
    const passed = ex?.records_passed ?? (applicable - failed);
    const passPct = applicable ? Math.round((passed / applicable) * 100) : 0;
    const cols = ex?.sample_violations?.length ? Object.keys(ex.sample_violations[0]) : [];
    const prov = !!preview.provisional;
    return (
      <div className="bc-rp">
        <div className="bc-rp-h">{prov ? "Rule that would be created if you confirm" : "Rule that will be created"}</div>
        {prov && <div className="bc-rp-prov">Prospective — this control isn't rule-eligible yet; confirming it creates this rule.</div>}
        {preview.executable && preview.preview
          ? <p className="bc-rp-pred">{preview.preview}</p>
          : <div className="bc-rp-nodata">{prov ? "Confirming this would create a " + (preview.compilation?.family || "").replace(/_/g, " ") + " rule, but it still needs input: " + (preview.compilation?.reason || "") + "." : (preview.execution_note || "Not compiled to a runnable rule.")}</div>}
        {preview.executable && (
          <div className="bc-rp-meta">
            {enf && <span className={"bc-chip " + (enf === "enforcing" ? "sub-ready" : "s-await")}>{prov ? enf + " (once confirmed)" : enf}</span>}
            {rule?.dimension && <span className="bc-chip">{rule.dimension}</span>}
            {rule?.family && <span className="bc-chip">{rule.family.replace(/_/g, " ")}</span>}
            {rule?.target_table && <span className="bc-chip">on {rule.target_table}</span>}
          </div>
        )}

        {preview.executable && (
          <>
            <div className="bc-rp-h" style={{ marginTop: 12 }}>{prov ? "If you confirm · impact on sample data" : "If approved · impact on sample data"}</div>
            {!ex || ex.status === "NO_DATA"
              ? <div className="bc-rp-nodata">No sample rows found{ex?.missing_tables?.length ? " for " + ex.missing_tables.join(", ") : ""} in <code>{ex?.sample_source || "data/ddm"}</code>.</div>
              : ex.status === "ERROR"
                ? <div className="bc-rp-nodata">Couldn't run on the sample: {ex.error}</div>
                : (
                  <>
                    <div className="bc-rp-bar" title={passed + " pass · " + failed + " fail of " + applicable}>
                      <div className="bc-rp-pass" style={{ width: passPct + "%" }} />
                      <div className="bc-rp-fail" style={{ width: (100 - passPct) + "%" }} />
                    </div>
                    <div className="bc-rp-stats">
                      <span className="ok">{passed.toLocaleString()} pass</span>
                      <span className={failed ? "no" : ""}>{failed.toLocaleString()} fail</span>
                      <span className="mut">{applicable.toLocaleString()} applicable · {Math.round((ex.violation_rate || 0) * 100)}% violations</span>
                      {ex.engine && <span className="mut">via {ex.engine}</span>}
                    </div>
                    {cols.length > 0 && (
                      <div className="bc-rp-tblwrap">
                        <div className="bc-rp-tbl-h">Sample violations</div>
                        <table className="bc-rp-tbl"><thead><tr>{cols.map((k) => <th key={k}>{k}</th>)}</tr></thead>
                          <tbody>{ex.sample_violations!.slice(0, 6).map((r, i) => <tr key={i}>{cols.map((k) => <td key={k}>{String(r[k] ?? "∅")}</td>)}</tr>)}</tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
          </>
        )}
      </div>
    );
  };

  const Lineage = ({ c }: { c: BcControlView }) => (
    <div className="bc-lin">
      <div className="bc-lin-h">Lineage · control → enterprise knowledge → lookup</div>
      {c.lineage?.reference_domains?.length > 0 && <div className="bc-lin-row"><span className="bc-lin-k">Reference domains</span>
        <span>{c.lineage.reference_domains.map((d) => (
          <button key={d.domain_id} className="bc-chip link" title="Open in Enterprise Knowledge → governed via lookup"
            onClick={() => onOpenEkd?.({ kind: "reference_domain", id: d.domain_id, lookup_entity_id: d.lookup_entity_id })}>
            {d.name} · {d.value_count} vals{d.lookup_entity_id ? " ↗ lookup" : ""}</button>))}</span></div>}
      {c.lineage?.fields?.length > 0 && <div className="bc-lin-row"><span className="bc-lin-k">Fields</span>
        <span>{c.lineage.fields.slice(0, 8).map((f) => (
          <button key={f.id} className="bc-chip link" title={f.id} onClick={() => onOpenEkd?.({ kind: "field", id: f.id })}>{f.name}</button>))}
          {c.lineage.fields.length > 8 && <span className="bc-more">+{c.lineage.fields.length - 8}</span>}</span></div>}
      {c.lineage?.knowledge_gap_ids?.length > 0 && <div className="bc-lin-row"><span className="bc-lin-k">Knowledge gaps</span>
        <span>{c.lineage.knowledge_gap_ids.map((g) => (
          <button key={g.id} className="bc-chip link" title={g.text} onClick={() => onOpenEkd?.({ kind: "gap", id: g.id })}>{g.id}</button>))}</span></div>}
    </div>
  );

  const readyState = (c: BcControlView) => c.review_state ? (
    <span className={"bc-sub " + (READY_SUB_CLS[c.review_state] || "")}>
      {c.review_state === "rule_ready" ? "rule ready" : c.review_state === "review_suggested" ? "review suggested" : "assessment pending"}
    </span>
  ) : null;

  const laneChip = (c: BcControlView) => c.lane_name ? <span className="bc-chip sm">{c.lane_name.replace(/_/g, " ")}</span> : null;

  const DetailActions = ({ c }: { c: BcControlView }) => {
    if (status === "needs_you") return (
      <div className="bc-d-acts">
        <button className="bc-mini ok" disabled={busy === c.control_id} onClick={() => act(c, "confirmed")}>Confirm as rule</button>
        <button className="bc-mini no" disabled={busy === c.control_id} onClick={() => act(c, "flagged")}>Reject</button>
        {c.status !== "pending" && <button className="bc-mini" disabled={busy === c.control_id} onClick={() => act(c, "pending")}>Reset</button>}
      </div>
    );
    if (status === "awaiting_values") return (
      <div className="bc-d-acts">
        {(c.lineage?.reference_domains || []).slice(0, 1).map((d) => (
          <button key={d.domain_id} className="bc-mini ok" onClick={() => onOpenEkd?.({ kind: "reference_domain", id: d.domain_id, lookup_entity_id: d.lookup_entity_id })}>Promote in reference data ↗</button>
        ))}
      </div>
    );
    return (
      <div className="bc-d-acts">
        {c.review_state === "review_suggested" && <button className="bc-mini ok" disabled={busy === c.control_id} onClick={() => act(c, "confirmed")}>Confirm</button>}
        <button className="bc-mini no" disabled={busy === c.control_id} onClick={() => act(c, "flagged")}>Flag for review</button>
        {c.status !== "pending" && <button className="bc-mini" disabled={busy === c.control_id} onClick={() => act(c, "pending")}>Clear flag</button>}
      </div>
    );
  };

  return (
    <div className="bc">
      <style>{CSS}</style>

      <div className="bc-top">
        <div><b>Business control · SME review</b><small> {entityLabel || entityId}</small></div>
        <div className="sp" />
        <button className="bc-btn" onClick={() => { loadSummary(); loadStatus(status, sub, laneF); }} title="Re-sync with the lookup layer and Enterprise Knowledge">↻ Refresh from lookup</button>
      </div>
      {flash && <div className="bc-flash">{flash}</div>}

      {summary && (
        <div className="bc-hdr">
          <div className="bc-hdr-top">
            <div className="bc-hdr-title">{summary.total} controls</div>
            <span className="sp" />
            <div className="bc-hdr-conf">{pct(summary.confidence)} confidence</div>
          </div>

          <Ribbon />
          <StatusTabs />

          <div className="bc-signals">
            {summary.review_suggested > 0 && (
              <button className="bc-nudge" onClick={() => goStatus("ready", "review_suggested")}>
                <span className="bc-nudge-i">◉</span>{summary.review_suggested} in Ready the agent flagged for a look
                <span className="bc-nudge-go">review →</span>
              </button>
            )}
            {summary.deviations.count > 0
              ? <button className="bc-dev warn" onClick={() => goStatus("needs_you")}>⚠ {summary.deviations.count} deviations — status disagrees with lane</button>
              : <span className="bc-dev ok">✓ No deviations — every control's status matches its lane</span>}
          </div>
        </div>
      )}

      <Facets />

      <div className={"bc-split" + (detail ? " open" : "")}>
        <div className="bc-list">
          {controls.map((c) => (
            <div key={c.control_id} className={"bc-item s-" + c.status + (detail?.control_id === c.control_id ? " sel" : "") + (c.deviation ? " dev" : "")}
              onClick={() => openDetail(c)}>
              <div className="bc-item-main">
                <div className="bc-item-t">{c.title}</div>
                <div className="bc-item-s">{c.category} · conf {pct(c.confidence)}{c.owner_field ? " · " + c.owner_field : ""}</div>
                <div className="bc-item-lin">
                  {laneChip(c)}
                  {readyState(c)}
                  {c.reference_status === "needs_values" && <span className="bc-sub sub-await">awaiting values</span>}
                  {c.deviation && <span className="bc-sub sub-dev">deviation</span>}
                </div>
              </div>
              {status === "needs_you" && (
                <div className="bc-item-acts" onClick={(e) => e.stopPropagation()}>
                  <button className="bc-mini ok" disabled={busy === c.control_id} onClick={() => act(c, "confirmed")}>Confirm</button>
                  <button className="bc-mini no" disabled={busy === c.control_id} onClick={() => act(c, "flagged")}>Reject</button>
                </div>
              )}
            </div>
          ))}
          {controls.length === 0 && <div className="bc-empty">Nothing here. Pick another status above.</div>}
        </div>

        {detail && (
          <aside className="bc-detail">
            <div className="bc-d-t">{detail.title}<button className="bc-x" onClick={() => setDetail(null)} aria-label="Close">✕</button></div>
            <div className="bc-d-chips">
              {detail.lane_name && <span className="bc-chip">lane · {detail.lane_name.replace(/_/g, " ")}</span>}
              <span className={"bc-chip " + STATUS_META[status].cls}>status · {STATUS_META[status].label}</span>
              {detail.review_state && <span className={"bc-chip " + (READY_SUB_CLS[detail.review_state] || "")}>{detail.review_state.replace(/_/g, " ")}</span>}
              <span className="bc-chip">conf {pct(detail.confidence)}</span>
            </div>

            {detail.business_statement && <div className="bc-d-sec"><b>Business rule</b><p>{detail.business_statement}</p></div>}
            {detail.control_logic && (detail.control_logic.left || detail.control_logic.operator) &&
              <div className="bc-d-sec"><b>Rule preview</b><p className="bc-logic">{detail.control_logic.left} <span>{detail.control_logic.operator || "→"}</span> {detail.control_logic.right}{detail.control_logic.value != null ? " = " + detail.control_logic.value : ""}</p></div>}
            {detail.expected_outcome && <div className="bc-d-sec"><b>Expected outcome</b><p>{detail.expected_outcome}</p></div>}
            {Array.isArray(detail.participating_fields) && detail.participating_fields.length > 0 &&
              <div className="bc-d-sec"><b>Fields &amp; roles</b><div className="bc-pf">{detail.participating_fields.map((p, i) => <span key={i} className="bc-pfi">{p.name}<em>{p.role}</em>{p.confidence != null ? <small>{Math.round(p.confidence * 100)}%</small> : null}</span>)}</div></div>}
            {(detail.reference_domain_names || []).length > 0 &&
              <div className="bc-d-sec"><b>Reference domain</b><p>{detail.reference_ready ? "✓ Governed via lookup — " : ""}{(detail.reference_domain_names || []).join(", ")}</p></div>}
            {Array.isArray(detail.evidence) && detail.evidence.length > 0 &&
              <div className="bc-d-sec"><b>Evidence</b>{detail.evidence.map((e, i) => <div key={i} className="bc-ev"><p>{e.statement}</p><span className="bc-ev-m">{e.type} · {e.authority} · conf {e.confidence != null ? Math.round(e.confidence * 100) + "%" : "—"}</span></div>)}</div>}
            {Array.isArray(detail.inferred_details) && detail.inferred_details.length > 0 &&
              <div className="bc-d-sec"><b>Agent assumptions</b>{detail.inferred_details.map((x, i) => <div key={i} className="bc-inf">{x.statement} <small>conf {x.confidence != null ? Math.round(x.confidence * 100) + "%" : "—"}</small></div>)}</div>}

            {(detail.readiness_assessment?.blocking_reasons || []).length > 0
              ? <div className="bc-d-verdict pend">Not yet executable — {(detail.readiness_assessment?.blocking_reasons || []).join(", ")}</div>
              : status === "ready" && detail.review_state === "rule_ready"
                ? <div className="bc-d-verdict ok">Ready — no blockers. Generates a rule on publish.</div>
                : status === "ready" && detail.review_state === "review_suggested"
                  ? <div className="bc-d-verdict rev">The agent suggests a quick review before this becomes a rule.</div>
                  : null}

            <RulePreview />
            <Lineage c={detail} />
            <DetailActions c={detail} />
          </aside>
        )}
      </div>
    </div>
  );
}

const CSS = `
.bc{--ink:#16306e;--line:#d9e0ea;--tx:#0f1923;--tx2:#5b6b7c;--gov:#166534;--gov-bg:#e9f6ee;--no:#991b1b;--no-bg:#fef2f2;--blue:#1d4ed8;--blue-bg:#eff4ff;--need:#b45309;--need-bg:#fff7ed;--sf:#fff;--sf2:#f5f8fc;font-family:'Sora',system-ui,sans-serif;color:var(--tx)}
.bc .sp{flex:1}
.bc-top{display:flex;align-items:center;padding:10px 2px}.bc-top small{color:var(--tx2)}
.bc-btn{border:1px solid var(--line);background:var(--sf);border-radius:8px;padding:8px 13px;font-size:12.5px;font-weight:600;cursor:pointer;font-family:inherit;color:var(--tx)}
.bc-btn:hover{background:var(--sf2)}
.bc-flash{background:var(--gov-bg);color:var(--gov);padding:6px 12px;border-radius:6px;font-size:12.5px;margin-bottom:8px}
.bc-hdr{background:var(--sf);border:1px solid var(--line);border-radius:12px;padding:12px 14px;margin-bottom:10px}
.bc-hdr-top{display:flex;align-items:baseline;margin-bottom:10px}
.bc-hdr-title{font-size:18px;font-weight:700}.bc-hdr-conf{font-size:12.5px;color:var(--tx2)}
.bc-ribbon{display:flex;height:12px;gap:2px;border-radius:7px;overflow:hidden;margin-bottom:10px}
.bc-seg{border:none;cursor:pointer;padding:0;transition:opacity .15s}.bc-seg:hover{opacity:.82}.bc-seg.on{outline:2px solid var(--ink);outline-offset:1px}
.bc-seg.s-need{background:#e79b3f}.bc-seg.s-await{background:#5b8def}.bc-seg.s-ready{background:#2f9e63}.bc-seg.s-excl{background:#b7c2cf}
.bc-stabs{display:flex;gap:8px;flex-wrap:wrap}
.bc-stab{display:flex;align-items:center;gap:8px;border:1px solid var(--line);background:var(--sf);border-radius:9px;padding:7px 12px;cursor:pointer;font-family:inherit;color:var(--tx)}
.bc-stab:hover{background:var(--sf2)}.bc-stab.on{border-color:var(--ink);background:var(--sf2)}
.bc-stab .bc-dot{width:9px;height:9px;border-radius:3px}
.bc-stab.s-need .bc-dot{background:#e79b3f}.bc-stab.s-await .bc-dot{background:#5b8def}.bc-stab.s-ready .bc-dot{background:#2f9e63}.bc-stab.s-excl .bc-dot{background:#b7c2cf}
.bc-stab-n{font-size:15px;font-weight:700}.bc-stab-l{font-size:12.5px;color:var(--tx2)}
.bc-stab.on .bc-stab-l{color:var(--tx)}
.bc-signals{display:flex;gap:10px;flex-wrap:wrap;align-items:center;margin-top:11px;padding-top:10px;border-top:1px dashed var(--line)}
.bc-nudge{display:inline-flex;align-items:center;gap:7px;border:1px solid #f0d6b0;background:var(--need-bg);color:var(--need);border-radius:20px;padding:5px 12px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit}
.bc-nudge-i{font-size:10px}.bc-nudge-go{color:var(--need);font-weight:700;margin-left:2px}
.bc-dev{font-size:12px;padding:5px 12px;border-radius:20px}
.bc-dev.ok{background:var(--gov-bg);color:var(--gov)}
.bc-dev.warn{background:var(--no-bg);color:var(--no);border:1px solid #e6c3c3;cursor:pointer;font-family:inherit;font-weight:600}
.bc-facets{display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-bottom:9px}
.bc-fc{border:1px solid var(--line);background:var(--sf);border-radius:20px;padding:5px 12px;font-size:12px;cursor:pointer;font-family:inherit;color:var(--tx2)}
.bc-fc:hover{background:var(--sf2)}.bc-fc.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.bc-fc.sub-review.on{background:var(--need);border-color:var(--need)}.bc-fc.sub-ready.on{background:var(--gov);border-color:var(--gov)}
.bc-search{border:1px solid var(--line);border-radius:8px;padding:6px 11px;font-size:12.5px;font-family:inherit;min-width:190px}
.bc-split{display:grid;grid-template-columns:1fr;gap:12px}
.bc-split.open{grid-template-columns:minmax(0,2fr) minmax(0,3fr)}
.bc-list{display:flex;flex-direction:column;gap:7px;max-height:660px;overflow:auto}
.bc-item{display:flex;gap:10px;border:1px solid var(--line);border-left:3px solid var(--line);border-radius:10px;padding:10px 12px;background:var(--sf);cursor:pointer}
.bc-item:hover{background:var(--sf2)}.bc-item.sel{outline:2px solid var(--blue);background:var(--blue-bg)}
.bc-item.s-confirmed{border-left-color:var(--gov)}.bc-item.s-flagged{border-left-color:var(--no)}.bc-item.dev{border-left-color:var(--need)}
.bc-item-main{flex:1;min-width:0}.bc-item-t{font-weight:600;font-size:13px;line-height:1.35}
.bc-item-s{font-size:11px;color:var(--tx2);margin-top:2px}
.bc-item-lin{display:flex;gap:5px;flex-wrap:wrap;margin-top:6px;align-items:center}
.bc-item-acts{display:flex;flex-direction:column;gap:5px}
.bc-sub{font-size:10px;font-weight:700;padding:2px 8px;border-radius:11px;text-transform:lowercase}
.sub-ready{background:var(--gov-bg);color:var(--gov)}.sub-review{background:var(--need-bg);color:var(--need)}.sub-pending{background:var(--sf2);color:var(--tx2)}
.sub-await{background:var(--blue-bg);color:var(--blue)}.sub-dev{background:var(--no-bg);color:var(--no)}
.bc-chip{font-size:10.5px;background:var(--sf2);border:1px solid var(--line);border-radius:12px;padding:2px 9px;color:var(--tx2)}
.bc-chip.sm{font-size:10px}.bc-chip.link{cursor:pointer;color:var(--blue);border-color:#c7d7f5;background:var(--blue-bg)}.bc-chip.link:hover{background:#e0ebff}
.bc-chip.s-need{background:var(--need-bg);color:var(--need);border-color:#f0d6b0}.bc-chip.s-ready{background:var(--gov-bg);color:var(--gov);border-color:#bfe3cd}.bc-chip.s-await{background:var(--blue-bg);color:var(--blue);border-color:#c7d7f5}.bc-chip.s-excl{background:var(--sf2);color:var(--tx2)}
.bc-chip.sub-review{background:var(--need-bg);color:var(--need)}.bc-chip.sub-ready{background:var(--gov-bg);color:var(--gov)}.bc-chip.sub-pending{background:var(--sf2);color:var(--tx2)}
.bc-more{font-size:10px;color:var(--tx2)}
.bc-mini{border:1px solid var(--line);background:var(--sf);border-radius:6px;padding:4px 11px;font-size:11.5px;cursor:pointer;font-family:inherit;white-space:nowrap;color:var(--tx)}
.bc-mini.ok{background:var(--gov);color:#fff;border-color:var(--gov)}.bc-mini.no{color:var(--no);border-color:#e6c3c3}
.bc-mini:disabled{opacity:.5;cursor:default}
.bc-detail{border:1px solid var(--line);border-radius:12px;padding:14px;background:var(--sf);max-height:660px;overflow:auto;position:sticky;top:8px}
.bc-d-t{font-weight:700;font-size:14.5px;display:flex;align-items:flex-start;gap:8px;line-height:1.3}.bc-x{margin-left:auto;border:none;background:none;cursor:pointer;color:var(--tx2);font-size:14px}
.bc-d-chips{display:flex;gap:6px;flex-wrap:wrap;margin:8px 0 12px}
.bc-d-sec{margin-bottom:10px}.bc-d-sec b{font-size:11px;text-transform:uppercase;color:var(--tx2);letter-spacing:.03em}.bc-d-sec p{font-size:12.5px;margin:3px 0 0;line-height:1.5}
.bc-logic{font-family:'JetBrains Mono',monospace;font-size:12px;background:var(--sf2);border:1px solid var(--line);border-radius:8px;padding:8px 10px}.bc-logic span{color:var(--blue);font-weight:700}
.bc-pf{display:flex;gap:6px;flex-wrap:wrap;margin-top:3px}.bc-pfi{font-size:11.5px;background:var(--sf2);border:1px solid var(--line);border-radius:7px;padding:3px 8px;display:inline-flex;gap:5px;align-items:center}.bc-pfi em{color:var(--tx2);font-style:normal;font-size:10px}.bc-pfi small{color:var(--gov)}
.bc-ev{background:var(--sf2);border-radius:6px;padding:6px 9px;margin-top:5px}.bc-ev p{margin:0;font-size:12px}.bc-ev-m{font-size:10.5px;color:var(--tx2)}
.bc-inf{font-size:12px;color:#7c5310;margin-top:4px}.bc-inf small{color:var(--tx2)}
.bc-d-verdict{font-size:12.5px;border-radius:8px;padding:9px 11px;margin:10px 0;display:flex;align-items:center;gap:7px}
.bc-d-verdict.ok{background:var(--gov-bg);color:var(--gov)}.bc-d-verdict.rev{background:var(--need-bg);color:var(--need)}.bc-d-verdict.pend{background:var(--sf2);color:var(--tx2)}
.bc-lin{background:var(--sf2);border:1px solid var(--line);border-radius:8px;padding:10px;margin-top:6px}
.bc-lin-h{font-size:11px;text-transform:uppercase;color:var(--ink);font-weight:700;margin-bottom:7px}
.bc-lin-row{display:flex;gap:8px;margin-bottom:6px;align-items:baseline}.bc-lin-k{font-size:11px;color:var(--tx2);min-width:120px}
.bc-lin-row>span:last-child{display:flex;gap:5px;flex-wrap:wrap}
.bc-d-acts{display:flex;gap:8px;margin-top:12px;flex-wrap:wrap}
.bc-empty{color:var(--tx2);font-size:12.5px;padding:28px;text-align:center}
.bc-rp{margin-top:12px;border:1px solid var(--line);border-radius:10px;padding:11px 12px;background:var(--sf2)}
.bc-rp-h{font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:var(--ink);font-weight:700;margin-bottom:6px}
.bc-rp-load{font-size:12px;color:var(--tx2)}
.bc-rp-prov{font-size:11.5px;color:var(--need);background:var(--need-bg);border:1px solid #f0d6b0;border-radius:7px;padding:6px 9px;margin-bottom:8px}
.bc-rp-pred{font-size:12.5px;margin:0 0 7px;line-height:1.45}
.bc-rp-meta{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:7px}
.bc-rp-dsl{margin:0;font-size:11px;color:var(--tx2);word-break:break-all}
.bc-rp-nodata{font-size:12px;color:var(--tx2);background:var(--sf);border:1px dashed var(--line);border-radius:7px;padding:8px 10px}
.bc-rp-nodata code{font-family:'JetBrains Mono',monospace;font-size:11px}
.bc-rp-bar{display:flex;height:12px;border-radius:7px;overflow:hidden;border:1px solid var(--line);margin:2px 0 6px}
.bc-rp-pass{background:#2f9e63}.bc-rp-fail{background:#d1655a}
.bc-rp-stats{display:flex;gap:12px;font-size:11.5px;flex-wrap:wrap}
.bc-rp-stats .ok{color:var(--gov);font-weight:600}.bc-rp-stats .no{color:var(--no);font-weight:600}.bc-rp-stats .mut{color:var(--tx2)}
.bc-rp-tblwrap{margin-top:9px;overflow:auto}
.bc-rp-tbl-h{font-size:10.5px;text-transform:uppercase;color:var(--tx2);margin-bottom:4px}
.bc-rp-tbl{border-collapse:collapse;font-size:11px;width:100%}
.bc-rp-tbl th{text-align:left;color:var(--tx2);font-weight:600;border-bottom:1px solid var(--line);padding:4px 8px;white-space:nowrap}
.bc-rp-tbl td{border-bottom:1px solid var(--line);padding:4px 8px;font-family:'JetBrains Mono',monospace;white-space:nowrap}
@media(max-width:820px){.bc-split.open{grid-template-columns:1fr}.bc-detail{position:static}}
`;
